/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Button, Grid, PageGridContainer, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import type { Maybe } from 'bundles/front-page/__generated__/baseContentfulTypes';
import type { MarketingContentFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import SeoPageHeader from 'bundles/front-page/components/modules/hero-banner/SeoPageHeader';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/front-page';

const imgParams = {
  quality: 40,
};

const styles = {
  gridContainer: css`
    ${breakpoints.down('sm')} {
      // place image on top of text on mobile
      > div {
        flex-direction: column-reverse;
      }

      text-align: center;
    }
  `,
  headerContainer: css`
    padding: var(--cds-spacing-200) 0 var(--cds-spacing-400);
  `,
  bucketContainer: css`
    ${breakpoints.down('xs')} {
      padding-bottom: var(--cds-spacing-50);
    }
  `,
  description: css`
    ${typography2.subtitleLarge}
    /* Override CDS styles weight */
    font-weight: 400;
    margin: var(--cds-spacing-300) 0;
  `,
  actionContainer: css`
    display: flex;
    gap: 16px;
    flex-direction: row;
    flex-wrap: wrap;

    ${breakpoints.down('sm')} {
      flex-direction: column;
      justify-content: center;
      padding-bottom: var(--cds-spacing-400);
    }
  `,
  cta: css`
    border-radius: 4px;
    max-width: unset;
    text-wrap: nowrap;
  `,
  heroContent: css`
    align-self: center;
  `,
  hidden: css`
    display: none;
  `,
  imageContainer: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  imageWrapper: css`
    img {
      /** 
       * override ImgixWithPrioritizedLoading inline max-width to allow the image to fill the container
       */
      max-width: 100% !important;
      width: 100%;

      ${breakpoints.down('sm')} {
        width: auto;
        max-height: 300px;
      }
    }
  `,
};

type Props = {
  content?: Maybe<MarketingContentFieldsFragment>;
  secondaryButtonLabel?: string | null;
  secondaryButtonLink?: string | null;
  backgroundColor?: string | null;
};

const HeroBannerRedesignWide = ({ content, secondaryButtonLabel, secondaryButtonLink, backgroundColor }: Props) => {
  const { description, callToActionLabel, callToActionLink, visual, title } = content || {};

  // Description, CTA, and visuals are required for this redesign
  if (!(content && description && callToActionLabel && visual)) {
    return null;
  }

  const isLOHPBucketTest = GrowthDiscoveryExperiments.get('LOHPBucketTest') !== 'control';

  return (
    <TrackedDiv
      trackingName="hero_module_cplus_promo"
      withVisibilityTracking={true}
      requireFullyVisible={false}
      trackClicks={false}
    >
      <section data-testid="front-page-banner">
        <PageGridContainer css={styles.gridContainer}>
          <Grid item md={6} xs={12} css={[styles.heroContent, backgroundColor ? { backgroundColor } : 'transparent']}>
            <div css={[styles.headerContainer, isLOHPBucketTest ? styles.bucketContainer : null]}>
              <SeoPageHeader />
              <Typography2 variant="titleLarge" component="h2">
                {title}
              </Typography2>
              <Typography2 variant="bodyPrimary" component="p" css={styles.description}>
                {description}
              </Typography2>
              <div css={[styles.actionContainer, isLOHPBucketTest ? styles.bucketContainer : null]}>
                <Button
                  component={TrackedLink2}
                  requireFullyVisible={false}
                  withVisibilityTracking={false}
                  trackingName="banner_cta"
                  css={styles.cta}
                  data-elementtiming="frontpage.banner.cta"
                  href={callToActionLink}
                  icon={<ArrowNextIcon />}
                  iconPosition="after"
                >
                  {callToActionLabel}
                </Button>
                <Button
                  component={TrackedLink2}
                  trackingName="enterprise_cta_double_blue"
                  withVisibilityTracking={false}
                  requireFullyVisible={false}
                  css={styles.cta}
                  variant="secondary"
                  href={secondaryButtonLink}
                  role="link"
                  icon={<ArrowNextIcon />}
                  iconPosition="after"
                >
                  {secondaryButtonLabel}
                </Button>
              </div>
            </div>
          </Grid>
          <Grid item md={6} xs={12} css={styles.imageContainer}>
            {visual?.url && (
              <div css={styles.imageWrapper}>
                <ImgixWithPrioritizedLoading
                  imgParams={imgParams}
                  src={visual?.url}
                  alt={visual.description ?? ''}
                  maxWidth={660}
                />
              </div>
            )}
          </Grid>
        </PageGridContainer>
      </section>
    </TrackedDiv>
  );
};

export default HeroBannerRedesignWide;
